import type { ApplicationType } from "./onboarding.types";

// Mirrors lendcord-core's OnboardingFieldPolicy.java field-for-field — keep
// both in sync when the backend's REQUIRED/ALLOWED maps change. Scoped to
// the 4 applicationTypes this form actually exposes (ENTERPRISE is a
// reachable backend target for an ADMIN caller via allowed-targets config,
// but deliberately out of this form's scope — see
// APPLICATION_TYPE_OPTIONS_BY_ROLE in OnboardingPage.tsx).
export type OnboardingField =
  | "SALUTATION"
  | "FIRST_NAME"
  | "MIDDLE_NAME"
  | "LAST_NAME"
  | "EMAIL"
  | "PHONE"
  | "DOB"
  | "AADHAAR"
  | "PAN"
  | "ADDRESSES"
  | "DOCUMENTS"
  | "TRANSACTION_TYPE"
  | "UPI_ID"
  | "LOCATION"
  | "REMARKS"
  | "BRANCH_ID"
  | "LOAN_AMOUNT";

// BRANCH_ID is required for STAFF/AGENT — both work out of a branch, unlike
// BORROWER/LENDER where it's merely allowed (recorded for provenance, never
// a hard requirement) — same reasoning as the backend policy's own comment.
const REQUIRED: Record<ApplicationType, readonly OnboardingField[]> = {
  BORROWER: ["FIRST_NAME", "EMAIL", "PHONE", "DOB", "AADHAAR", "ADDRESSES", "LOAN_AMOUNT"],
  LENDER: ["FIRST_NAME", "EMAIL", "PHONE"],
  STAFF: ["FIRST_NAME", "EMAIL", "PHONE", "BRANCH_ID"],
  AGENT: ["FIRST_NAME", "EMAIL", "PHONE", "BRANCH_ID"],
};

// STAFF/AGENT are internal team members, not loan counterparties — they
// carry none of the loan/transaction/location fields.
const ALLOWED: Record<ApplicationType, readonly OnboardingField[]> = {
  BORROWER: [
    "SALUTATION", "FIRST_NAME", "MIDDLE_NAME", "LAST_NAME", "EMAIL", "PHONE", "DOB", "AADHAAR", "PAN",
    "ADDRESSES", "DOCUMENTS", "TRANSACTION_TYPE", "UPI_ID", "LOCATION", "REMARKS", "BRANCH_ID", "LOAN_AMOUNT",
  ],
  LENDER: [
    "SALUTATION", "FIRST_NAME", "MIDDLE_NAME", "LAST_NAME", "EMAIL", "PHONE", "DOB", "AADHAAR", "PAN",
    "ADDRESSES", "DOCUMENTS", "TRANSACTION_TYPE", "UPI_ID", "LOCATION", "REMARKS", "BRANCH_ID", "LOAN_AMOUNT",
  ],
  STAFF: ["SALUTATION", "FIRST_NAME", "MIDDLE_NAME", "LAST_NAME", "EMAIL", "PHONE", "DOB", "AADHAAR", "PAN", "ADDRESSES", "DOCUMENTS", "BRANCH_ID"],
  AGENT: ["SALUTATION", "FIRST_NAME", "MIDDLE_NAME", "LAST_NAME", "EMAIL", "PHONE", "DOB", "AADHAAR", "PAN", "ADDRESSES", "DOCUMENTS", "BRANCH_ID"],
};

export function isFieldRequired(type: ApplicationType | "", field: OnboardingField): boolean {
  return type !== "" && REQUIRED[type].includes(field);
}

export function isFieldAllowed(type: ApplicationType | "", field: OnboardingField): boolean {
  return type !== "" && ALLOWED[type].includes(field);
}
